import "./FlaggedNames.scss";

import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { MdOpenInNew } from "react-icons/md";

import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import TableColumn from "../../components/TableColumn/TableColumn";
import api from "../../services/api";

// Admin "Flagged Names" tab. Powered by GET /admin/flagged-names, which lists
// accounts whose signup name looks like keyboard mash (name_flagged, set by
// the server's nameQuality check). Read-only review surface: nothing is
// auto-blocked or deleted — open a profile and handle it in the Users tab.
function FlaggedNames() {
  const records = 25;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const columns = useMemo(
    () => ["Name", "Why flagged", "Email", "Phone", "Created"],
    [],
  );

  const fetchFlagged = async (page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/flagged-names", {
        params: { page, records },
      });
      const list = data?.data || [];
      const formatted = list.map((u) => ({
        _id: u._id,
        id: u._id,
        Name: [u.firstName, u.lastName].filter(Boolean).join(" ") || "(no name)",
        "Why flagged": prettyReason(u.name_flag_reason),
        Email: u.email || "—",
        Phone: u.phone || "—",
        Created: safeDate(u.created_at),
      }));
      setRows(formatted);
      // TableColumn wants the RAW record count and divides by `records` itself
      // (same contract as Users / User Inboxes / Shared Devices).
      setTotal(Number(data?.total) || 0);
    } catch (err) {
      console.error("FlaggedNames fetch failed:", err);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlagged(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const actions = useMemo(
    () => [
      {
        label: <MdOpenInNew size={20} color="#4F30EA" />,
        onClick: (rowData) => navigate(`/users/${rowData._id}`),
      },
    ],
    [navigate],
  );

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="flagged-names">
            <p className="flagged-names__hint">
              Accounts whose signup name looks like keyboard mash. Flagged for
              review only — nothing is auto-blocked or deleted. Open a profile to
              handle it in the Users tab.
            </p>

            <TableColumn
              title="Flagged Names"
              columns={columns}
              data={rows}
              loading={loading}
              totalPages={total}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              records={records}
              actions={actions}
            />
          </section>
        </article>
      </div>
    </main>
  );
}

// name_flag_reason looks like "bdhdhnsndbdbs:no_vowels,dhshshshssv:no_vowels".
// Turn it into something an admin can read at a glance.
function prettyReason(reason) {
  if (!reason) return "—";
  const labels = {
    no_vowels: "no vowels",
    consonant_run: "long consonant run",
    repeated_bigram: "repeated letters",
    sparse_vowels: "very few vowels",
    blocklist: "junk placeholder",
  };
  return reason
    .split(",")
    .map((part) => {
      const [token, code] = part.split(":");
      return `${token} (${labels[code] || code})`;
    })
    .join(", ");
}

function safeDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "—" : format(d, "MMM d, yyyy HH:mm");
}

export default FlaggedNames;
